/**
 * Firebase re-authentication before destructive account actions.
 */
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  type User,
} from 'firebase/auth';
import { auth } from '@/firebase';

export async function reauthenticateCurrentUser(password?: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  const providerId = user.providerData[0]?.providerId ?? '';

  if (providerId === 'password') {
    const email = user.email;
    if (!email) throw new Error('Email is required for re-authentication');
    if (!password?.trim()) throw new Error('Password is required');
    const credential = EmailAuthProvider.credential(email, password);
    await reauthenticateWithCredential(user, credential);
    return;
  }

  if (providerId === 'google.com') {
    await reauthenticateWithPopup(user, new GoogleAuthProvider());
    return;
  }

  if (providerId.includes('apple')) {
    await reauthenticateWithPopup(user, new OAuthProvider('apple.com'));
    return;
  }

  const oauthProvider = new OAuthProvider(providerId);
  await reauthenticateWithPopup(user, oauthProvider);
}

export function userRequiresPasswordReauth(user: User | null): boolean {
  if (!user) return false;
  return user.providerData.some((p) => p.providerId === 'password');
}
